// frontend/src/components/game/panels/Minimap/Minimap.jsx
import React, { useMemo, useRef, useEffect, useState } from 'react';
import useGameStore from '@store/useGameStore';
import useMapStore from '@store/useMapStore';
import { getTeamColor } from '@utils/cssVariables';
import { getTileSize } from '@utils/constants';
import { useBreakpoint } from '@utils/responsive';
import { EMPTY_ARRAY } from '@utils/constants';
import './Minimap.css';

// Terrain colors for minimap (simplified)
const TERRAIN_COLORS = {
  grass: '#4a7c4e',
  water: '#3b82a0',
  mountain: '#6b5b4f',
  forest: '#2d5a3d',
  sand: '#c9b896',
  rock: '#5a5a5a',
  void: '#1a1a1a',
  default: '#3d3d3d',
};

/**
 * Minimap component - Shows a small overview of the battlefield
 * Supports click-to-center functionality
 */
export default function Minimap() {
  const game = useGameStore((s) => s.game);
  const tiles = useMapStore.getState().selectTerrainTiles();
  const centerOnTile = useMapStore.getState().centerOnTile;

  const breakpoint = useBreakpoint();
  const tileSize = getTileSize(breakpoint);

  const containerRef = useRef(null);
  const [viewportRect, setViewportRect] = useState(null);

  // Calculate minimap dimensions
  const mapWidth = game?.map?.width ?? 0;
  const mapHeight = game?.map?.height ?? 0;

  // Calculate tile size for minimap (fit within container)
  const MINIMAP_MAX_WIDTH = 230; // Leave some padding
  const miniTileSize = mapWidth > 0 ? Math.floor(MINIMAP_MAX_WIDTH / mapWidth) : 8;
  const minimapWidth = mapWidth * miniTileSize;
  const minimapHeight = mapHeight * miniTileSize;

  // Get units from game
  const units = useMemo(() => {
    if (!game?.dynamic_objects?.units) return EMPTY_ARRAY;
    return game.dynamic_objects.units.filter(u => u.x !== undefined && u.y !== undefined);
  }, [game?.dynamic_objects?.units]);

  // Update viewport indicator
  useEffect(() => {
    const updateViewport = () => {
      const mapArea = document.querySelector('.map-area');
      if (!mapArea || mapWidth === 0) return;

      const { scrollLeft, scrollTop, clientWidth, clientHeight } = mapArea;
      const fullMapWidth = mapWidth * tileSize;
      const fullMapHeight = mapHeight * tileSize;

      // Calculate viewport position as percentage
      const viewX = (scrollLeft / fullMapWidth) * minimapWidth;
      const viewY = (scrollTop / fullMapHeight) * minimapHeight;
      const viewW = (clientWidth / fullMapWidth) * minimapWidth;
      const viewH = (clientHeight / fullMapHeight) * minimapHeight;

      setViewportRect({
        left: Math.max(0, viewX),
        top: Math.max(0, viewY),
        width: Math.min(viewW, minimapWidth - viewX),
        height: Math.min(viewH, minimapHeight - viewY),
      });
    };

    const mapArea = document.querySelector('.map-area');
    if (mapArea) {
      updateViewport();
      mapArea.addEventListener('scroll', updateViewport);
      window.addEventListener('resize', updateViewport);
    }

    return () => {
      if (mapArea) {
        mapArea.removeEventListener('scroll', updateViewport);
      }
      window.removeEventListener('resize', updateViewport);
    };
  }, [mapWidth, mapHeight, tileSize, minimapWidth, minimapHeight]);

  // Handle click on minimap
  const handleMinimapClick = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Convert to tile coordinates
    const tileX = Math.floor(clickX / miniTileSize);
    const tileY = Math.floor(clickY / miniTileSize);

    // Center map on clicked tile
    centerOnTile(tileX, tileY, tileSize);
  };

  if (!game?.map || tiles.length === 0) {
    return (
      <div className="minimap-container minimap-empty">
        <p>No map data</p>
      </div>
    );
  }

  return (
    <div className="minimap-container">
      <div
        ref={containerRef}
        className="minimap-grid"
        style={{
          width: `${minimapWidth}px`,
          height: `${minimapHeight}px`,
          '--mini-tile-size': `${miniTileSize}px`,
        }}
        onClick={handleMinimapClick}
      >
        {/* Terrain tiles */}
        {tiles.map(({ x, y, terrain }) => (
          <div
            key={`${x}-${y}`}
            className="minimap-tile"
            style={{
              left: `${x * miniTileSize}px`,
              top: `${y * miniTileSize}px`,
              width: `${miniTileSize}px`,
              height: `${miniTileSize}px`,
              backgroundColor: TERRAIN_COLORS[terrain] || TERRAIN_COLORS.default,
            }}
          />
        ))}

        {/* Units */}
        {units.map((unit) => (
          <div
            key={`unit-${unit.id}`}
            className="minimap-unit"
            style={{
              left: `${unit.x * miniTileSize + miniTileSize * 0.2}px`,
              top: `${unit.y * miniTileSize + miniTileSize * 0.2}px`,
              width: `${miniTileSize * 0.6}px`,
              height: `${miniTileSize * 0.6}px`,
              backgroundColor: getTeamColor(unit.teamClass),
            }}
            title={unit.unit_template?.name || unit.name}
          />
        ))}

        {/* Viewport indicator */}
        {viewportRect && (
          <div
            className="minimap-viewport"
            style={{
              left: `${viewportRect.left}px`,
              top: `${viewportRect.top}px`,
              width: `${viewportRect.width}px`,
              height: `${viewportRect.height}px`,
            }}
          />
        )}
      </div>

      <p className="minimap-hint">Click to navigate</p>
    </div>
  );
}
